/**
 * Filling a run with cabinets, and keeping it filled while a finger moves them.
 *
 * This is the file the whole tool is correct or incorrect because of. A homeowner designing
 * unattended must never be able to send their kitchen maker something the maker cannot
 * build, and the way that is guaranteed here is by construction rather than by validation:
 * a run is ALWAYS exactly full, every width always comes from the maker's own range or from
 * a stretch they have said they will cut, and the arithmetic is checked rather than trusted.
 *
 * Pure functions on plain data, deliberately. No three.js, no Vue, no DOM. It is the part
 * most worth testing and the part least worth debugging through a renderer.
 */

/** Millimetres of slop we treat as zero. Nobody cuts to a tenth. */
const EPS = 0.5

/** The 32mm system's step. Widths that land on it are the ones a factory is set up for. */
export const STEP = 32

export const sum = (items) => items.reduce((n, i) => n + i.w, 0)

/**
 * Fill a length with cabinets, largest first.
 *
 * Largest-first rather than a search for the neatest combination, because a kitchen is read
 * left to right and a run of 900, 600, 450 looks deliberate where 450, 600, 900 looks like
 * something went wrong. The remainder is settled by `settle` below.
 */
export function fill(length, specs, opts = {}) {
  const usable = specs
    .filter((s) => !s.role || s.role !== 'filler')
    .slice()
    .sort((a, b) => b.w - a.w)

  if (usable.length === 0) return { items: [], remainder: length, problem: 'no cabinets' }

  const items = []
  let left = length

  for (const spec of usable) {
    while (left - spec.w >= -EPS) {
      items.push({ specId: spec.id, w: spec.w })
      left -= spec.w
    }
  }

  return settle(items, left, usable, opts)
}

/**
 * What to do with the piece left over.
 *
 * Three answers, in this order, and the order is the whole policy:
 *
 *   1. Nothing, when it is already zero.
 *   2. Stretch one cabinet, if the maker will cut it at that width. Cheapest and invisible:
 *      a 630 where a 600 was is a cabinet, not a strip of board.
 *   3. A filler, if the gap is wide enough for the maker to make one.
 *
 * And when none of those work, it SAYS SO. A run that cannot be filled honestly is worth
 * more than one that silently leaves 18mm for somebody to discover on site.
 */
function settle(items, left, specs, opts) {
  const minFiller = opts.minFiller ?? 30
  const maxWidth = opts.maxCabinetWidth ?? Infinity

  if (left <= EPS) return { items, remainder: 0, problem: null }

  // 2. Stretch the widest cabinet that can absorb it.
  const byWidth = items
    .map((item, i) => ({ item, i, spec: specs.find((s) => s.id === item.specId) }))
    .filter((x) => x.spec)
    .sort((a, b) => b.item.w - a.item.w)

  for (const { item, spec } of byWidth) {
    const room = Math.min(spec.stretch[1], maxWidth) - item.w
    if (room >= left - EPS) {
      item.w += left
      item.stretched = true
      return { items, remainder: 0, problem: null }
    }
  }

  // 3. A filler, if it is wide enough to be one.
  if (left >= minFiller) {
    items.push({ specId: 'filler', w: left, filler: true })
    return { items, remainder: 0, problem: null }
  }

  // Nothing honest is left to do with it.
  return {
    items,
    remainder: left,
    problem: `${Math.round(left)}mm cannot be filled: too wide to ignore, too narrow for a `
           + `${minFiller}mm filler, and no cabinet stretches that far`,
  }
}

/**
 * Make a run exactly `length` long, keeping the cabinets already standing in it.
 *
 * This is what a room resize does to every run in the kitchen. `fill` cannot be used for it:
 * fill starts from nothing, so typing 4300 where 4200 was would throw away an arrangement
 * somebody had just spent five minutes on and hand back a fresh row of largest-first boxes.
 *
 * So the existing order is kept, whatever still fits stays where it is, and only the
 * DIFFERENCE is dealt with -- by the same policy as everywhere else: catalogue sizes first,
 * then a stretch, then a filler, and a stated problem when none of those is honest.
 *
 * Fillers are not kept. A filler is not a thing somebody chose, it is what was left over last
 * time, and carrying it into a room of a different size preserves an accident.
 */
export function refit(items, length, specs, opts = {}) {
  const usable = specs
    .filter((s) => !s.role || s.role !== 'filler')
    .slice()
    .sort((a, b) => b.w - a.w)

  if (usable.length === 0) return { items: [], remainder: length, problem: 'no cabinets' }

  // Everything back to its catalogue width. A stretch is an answer to the OLD length, and
  // carrying it over means the arithmetic below is settling a remainder twice.
  const kept = []
  let used = 0
  for (const item of items) {
    if (item.filler) continue
    const spec = usable.find((s) => s.id === item.specId)
    if (!spec) continue
    if (used + spec.w > length + EPS) continue   // the wall got shorter: this one goes
    kept.push({ ...item, w: spec.w, stretched: false })
    used += spec.w
  }

  // The wall got longer: fill what opened up, largest first, as fill does.
  let left = length - used
  for (const spec of usable) {
    while (left - spec.w >= -EPS) {
      kept.push({ specId: spec.id, w: spec.w })
      left -= spec.w
    }
  }

  return settle(kept, left, usable, opts)
}

/**
 * Move the item at `from` so its left edge sits at `x`, and push what it meets.
 *
 * This is what a finger does. The run stays exactly as long as it was and exactly as full,
 * so there is no state in which the design is briefly invalid -- which matters because the
 * renderer draws every frame of the drag, not just the end of it.
 *
 * Returns a new item order. The caller keeps the old one until the gesture is released, so
 * undo is free.
 */
export function slide(items, from, x) {
  const moved = items[from]
  if (!moved) return items

  const rest = items.filter((_, i) => i !== from)

  // Where does it land, counted in whole neighbours rather than millimetres? Anything else
  // makes a cabinet swap places twice while your finger is still on it.
  let at = 0
  let seen = 0
  for (const item of rest) {
    if (seen + item.w / 2 > x) break
    seen += item.w
    at += 1
  }

  rest.splice(at, 0, moved)
  return rest
}

/** Where each item starts, along the run. */
export function offsets(items) {
  const out = []
  let x = 0
  for (const item of items) {
    out.push(x)
    x += item.w
  }
  return out
}

/**
 * Is this run something the maker can actually build?
 *
 * Called after every change, and its answer is shown rather than stored: a design is not
 * saved as invalid, it is prevented from becoming invalid. This exists to catch the case
 * where that prevention has a hole in it, which is exactly the bug worth finding early.
 */
export function faults(run, specs, limits) {
  const out = []
  const length = run.to - run.from
  const total = sum(run.items)

  if (Math.abs(total - length) > EPS) {
    out.push(`the cabinets add up to ${Math.round(total)}mm in a ${Math.round(length)}mm run`)
  }

  for (const item of run.items) {
    // A gap is deliberate nothing -- the overhead run above a fridge or a pantry, which is
    // already full height. It has a width and no other opinion.
    if (item.gap) continue
    if (item.filler) {
      if (item.w < limits.minFiller - EPS) {
        out.push(`a ${Math.round(item.w)}mm filler is under the ${limits.minFiller}mm minimum`)
      }
      continue
    }
    const spec = specs.find((s) => s.id === item.specId)
    if (!spec) { out.push(`${item.specId} is not in this maker's range`); continue }
    if (item.w > limits.maxCabinetWidth + EPS) {
      out.push(`${spec.name} at ${Math.round(item.w)}mm is over the `
             + `${limits.maxCabinetWidth}mm this maker builds`)
    }
    if (item.w < spec.stretch[0] - EPS || item.w > spec.stretch[1] + EPS) {
      out.push(`${spec.name} at ${Math.round(item.w)}mm is outside its `
             + `${spec.stretch[0]}-${spec.stretch[1]}mm range`)
    }
  }

  return out
}

/**
 * A kitchen, rather than a row of identical boxes.
 *
 * `fill` is the general engine and it is deliberately dumb: largest first, remainder settled.
 * That is right when a person is rearranging a run and wrong as the first thing they ever
 * see. Four 900s in a row is arithmetically perfect and reads as a warehouse -- and the
 * first three seconds of this tool are a sales pitch, not a CAD session.
 *
 * So a starter run gets the things every kitchen has, in the order a kitchen has them: a sink
 * somewhere near the middle, a cooktop away from it, drawers beside the cooktop because that
 * is where pans live. Whatever is left over is filled the ordinary way.
 */
export function compose(length, specs, opts = {}) {
  // A kitchen has ONE sink and ONE cooktop, however many runs it has. Composing each run
  // independently gave an L-shaped kitchen two of each, which is the kind of mistake that
  // makes a homeowner stop trusting everything else on the screen.
  const taken = opts.taken ?? new Set()
  const find = (role) => (taken.has(role) ? null : specs.find((s) => s.role === role))
  const drawers = specs.find((s) => s.drawers > 0 && !s.role)

  const wanted = [find('sink'), find('cooktop'), drawers].filter(Boolean)
  const essentials = []
  let used = 0
  for (const spec of wanted) {
    if (used + spec.w > length) break
    essentials.push({ specId: spec.id, w: spec.w })
    if (spec.role) taken.add(spec.role)
    used += spec.w
  }
  if (essentials.length === 0) return fill(length, specs, opts)

  const rest = fill(length - used, specs.filter((s) => !s.role), opts)

  // The sink wants to be roughly central and the cooktop away from it. Interleaving the
  // plain cabinets between them is enough to look considered without pretending to be a
  // kitchen designer.
  const items = []
  const plain = rest.items
  const gap = Math.max(1, Math.floor(plain.length / (essentials.length + 1)))
  let e = 0
  for (let i = 0; i < plain.length; i++) {
    items.push(plain[i])
    if ((i + 1) % gap === 0 && e < essentials.length) items.push(essentials[e++])
  }
  while (e < essentials.length) items.push(essentials[e++])

  return { items, remainder: rest.remainder, problem: rest.problem }
}

/**
 * Lay one sequence of cabinets along a rail with bends in it.
 *
 * A chain is several runs that meet at corners, and to a finger it is ONE rail: drag a
 * cabinet toward the corner and the cabinets round the corner should make way, the way
 * beads do on a wire. So the chain is treated as a single sequence, cut into legs, and each
 * leg is then settled by the same policy as everywhere else -- shrink or stretch a
 * neighbour within what the maker will cut, else a filler, else say so.
 *
 * WHERE to cut is the whole problem. Cutting at the corner by midpoints and settling what
 * lands on each side fails whenever the cabinet that straddles the corner is bigger than
 * the neighbours' give -- a 900 on the corner of two legs with 230mm of headroom between
 * them is "160mm more cabinet than wall" however the two are settled. So for each leg the
 * cut is CHOSEN: the one nearest the midpoint cut at which that leg settles, tried outward
 * in both directions. Greedy leg by leg, which is enough because a rail has two or three
 * legs and a kitchen has a dozen cabinets.
 *
 * `sequence` is every cabinet in chain order; `lengths` is each leg's usable length. Returns
 * one list per leg, and a problem when the whole thing does not fit -- in which case the
 * caller keeps what it had, because a rail that cannot hold its cabinets is not a state a
 * kitchen can be in.
 */
export function reflow(sequence, lengths, specs, opts = {}) {
  const minFiller = opts.minFiller ?? 30
  const maxWidth = opts.maxCabinetWidth ?? Infinity
  const locked = opts.locked ?? new Set()
  const specOf = (it) => specs.find((s) => s.id === it.specId)

  // Back to catalogue width and no fillers: a stretch was an answer to the old legs. Each
  // item remembers which leg it came from, so a locked one can be kept there.
  let rest = sequence.filter((it) => !it.filler).map((it) => {
    const sp = specOf(it)
    return { ...it, w: sp ? sp.w : it.w, stretched: false }
  })

  /** One leg, given its cabinets: settled to exactly L, or null. Never mutates its input. */
  const settleLeg = (items, L) => {
    const out = items.map((it) => ({ ...it }))
    let total = sum(out)
    if (total - L > EPS) {
      // Shrink, most headroom first, never below what the maker cuts.
      let over = total - L
      const room = out
        .map((it) => ({ it, floor: specOf(it)?.stretch[0] ?? it.w }))
        .sort((a, b) => (b.it.w - b.floor) - (a.it.w - a.floor))
      for (const x of room) {
        const take = Math.min(Math.max(0, x.it.w - x.floor), over)
        if (take <= 0) continue
        x.it.w -= take; x.it.stretched = true
        over -= take
        if (over <= EPS) break
      }
      if (over > EPS) return null
      return out
    }
    let left = L - total
    if (left > EPS) {
      const room = out
        .map((it) => ({ it, cap: Math.min(specOf(it)?.stretch[1] ?? it.w, maxWidth) }))
        .sort((a, b) => (b.cap - b.it.w) - (a.cap - a.it.w))
      for (const x of room) {
        const add = Math.min(Math.max(0, x.cap - x.it.w), left)
        if (add <= 0) continue
        x.it.w += add; x.it.stretched = true
        left -= add
        if (left <= EPS) break
      }
      if (left > EPS) {
        if (left < minFiller) return null
        out.push({ specId: 'filler', w: left, filler: true })
      }
    }
    return out
  }

  /**
   * What a designer does when it will not go: "I'd make that a 600".
   *
   * When no cut settles, the plain cabinet nearest the trouble is swapped for the next size
   * down in the same range, and the cut is tried again. Never a sink, a cooktop, a corner
   * unit or anything else with a job -- only a cupboard or a drawer bank, which come in
   * every width the maker sells. Reported back, so the screen can say what it did and undo
   * can take it back.
   */
  const swapped = []
  // One size down, once, for at most two cabinets. Beyond that it is not "I'd make that a
  // 600", it is redesigning the kitchen behind someone's back, and the honest answer is no.
  const smaller = (it) => {
    if (swapped.length >= 2 || swapped.some((x) => x.id === it.id)) return null
    const sp = specOf(it)
    if (!sp || sp.role || it.filler) return null
    const cands = specs
      .filter((c) => !c.role && c.kind === sp.kind && c.w < sp.w
        && (c.drawers > 0) === (sp.drawers > 0))
      .sort((a, b) => b.w - a.w)
    return cands[0] ?? null
  }
  const downsize = (items, fromEnd) => {
    const order = fromEnd ? items.slice().reverse() : items
    for (const it of order) {
      const sp = smaller(it)
      if (!sp) continue
      swapped.push({ id: it.id, from: it.specId, to: sp.id })
      it.specId = sp.id; it.w = sp.w
      return true
    }
    return false
  }

  const legs = []
  for (let k = 0; k < lengths.length; k++) {
    const L = lengths[k]
    if (k === lengths.length - 1) {
      // The last leg takes everything that is left, or the rail does not fit.
      let done = settleLeg(rest, L)
      // Over: shrink a cupboard to the next size down, nearest the corner first, up to twice.
      for (let tries = 0; !done && tries < 2 && sum(rest) > L; tries++) {
        if (!downsize(rest, false)) break
        done = settleLeg(rest, L)
      }
      if (!done) {
        const total = sum(rest)
        return { legs, problem: total > L
          ? `${Math.round(total - L)}mm more cabinet than wall`
          : `${Math.round(L - total)}mm cannot be filled`, swapped }
      }
      legs.push(done)
      rest = []
      break
    }
    // The midpoint cut, then outward from it: n, n-1, n+1, n-2, n+2 ...
    let seen = 0, n = 0
    for (const it of rest) { if (seen + it.w / 2 > L) break; seen += it.w; n += 1 }
    let done = null, cut = n
    const after = lengths.slice(k + 1).reduce((a, b) => a + b, 0)
    for (let d = 0; d <= rest.length && !done; d++) {
      for (const c of d === 0 ? [n] : [n - d, n + d]) {
        if (c < 0 || c > rest.length) continue
        // Never leave a later leg with nothing when it has length to fill: a run that is
        // there is a run with cabinets on it.
        if (c === rest.length && lengths.length - 1 > k) continue
        // A locked cabinet stays on the leg the homeowner locked it on: no cut that would
        // carry one across the corner, in either direction.
        if (rest.slice(0, c).some((it) => locked.has(it.id) && it.leg !== undefined && it.leg !== k)) continue
        if (rest.slice(c).some((it) => locked.has(it.id) && it.leg === k)) continue
        // And never take a cut that settles THIS leg by starving the next: what is left has
        // to be able to fit what is left, shrunk as far as the maker allows.
        const remainder = rest.slice(c)
        const floorOf = remainder.reduce((a, it) => a + (specOf(it)?.stretch[0] ?? it.w), 0)
        if (floorOf > after + EPS) continue
        const tryLeg = settleLeg(rest.slice(0, c), L)
        if (tryLeg) { done = tryLeg; cut = c; break }
      }
    }
    if (!done) {
      // Nothing near the corner settles: make the cabinet on the corner the next size down
      // and try this leg again.
      if (downsize(rest.slice(0, Math.max(n, 1)), true)) { k -= 1; continue }
      return { legs, problem: `nothing fits the ${Math.round(L)}mm leg`, swapped }
    }
    legs.push(done)
    rest = rest.slice(cut)
  }
  return { legs, problem: null, swapped }
}
